import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { MapPin, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import {
  getCity, getCityBySlug, cityUrl, cityCategoryUrl, RESERVED_SLUGS,
  getNearbyCitiesInState, getAllCitiesInState,
} from "../lib/cities";
import { CATEGORIES, getCategory, getCategoryByUrlSlug } from "../lib/categories";
import { placeUrl } from "../lib/placeUrl";
import ParkCard from "../components/ParkCard";
import PrivateParkCard from "../components/PrivateParkCard";
import SponsoredResults from "../components/SponsoredResults";
import { getPrivateParksByCity } from "../data/privateParks";
import JsonLd from "../components/JsonLd";
import Breadcrumbs from "../components/Breadcrumbs";
import SuggestPlaceForm from "../components/SuggestPlaceForm";
import SuggestPlaceBanner from "../components/SuggestPlaceBanner";
import {
  isPRCity,
  getPRBilingualIntro,
  getCategoryLabelES,
  getPRFeaturedGuide,
} from "../data/prBilingualContent";

const SITE_URL = "https://pawground.app";

// EmptyStateHelp — shown in place of the results grid when a city or
// city+category page has zero cached places. Design invariant: NEVER
// link the user toward another empty page. The `/cities/empty-state-context`
// endpoint returns only cities/categories that clear the same
// `MIN_PLACES_TO_INDEX` bar we use elsewhere, so every anchor here
// leads somewhere with real content.
//
// Kept inline in CityPage.jsx rather than a new file — it's tightly
// coupled to the empty branch of this single component and unlikely
// to be reused. If a second consumer ever surfaces, promote it.
const EmptyStateHelp = ({ city, category }) => {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/cities/empty-state-context", {
        params: {
          slug: city.slug,
          category: category ? category.urlSlug : undefined,
          limit: 4,
        },
      })
      .then((r) => {
        if (!cancelled) setCtx(r.data || null);
      })
      .catch(() => {
        if (!cancelled) setCtx(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [city.slug, category?.urlSlug]);

  const nearby = (ctx && ctx.nearby_with_category) || [];
  const others = (ctx && ctx.other_categories_in_city) || [];
  // Resolve keys → CATEGORIES entries. seo_listings tile keys use the
  // 'key' field (e.g. "park"), so we match on that first, then fall
  // back to urlSlug for defensiveness against future rename mismatches.
  const otherCategoriesResolved = others
    .map((o) => {
      const found = CATEGORIES.find(
        (c) => c.key === o.key || c.urlSlug === o.key,
      );
      return found ? { ...found, count: o.count } : null;
    })
    .filter(Boolean);

  return (
    <div
      className="bg-white rounded-3xl p-8 border border-brand-border-warm"
      data-testid="city-empty-help"
    >
      <div className="font-heading text-xl font-semibold text-brand-ink">
        {category
          ? `We don't have any ${category.seoShort} listed in ${city.name} yet`
          : `We don't have any places listed in ${city.name} yet`}
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-brand-ink/50">Loading suggestions…</div>
      ) : (
        <div className="mt-5 space-y-4">
          {category && nearby.length > 0 && (
            <div data-testid="empty-nearby-with-category">
              <div className="text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/50 mb-2">
                Browse nearby
              </div>
              <div className="flex flex-wrap gap-2">
                {nearby.map((n) => (
                  <Link
                    key={n.slug}
                    to={`/${category.urlSlug}/${n.slug}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold bg-brand-light-green/50 text-brand-forest hover:bg-brand-light-green transition-colors"
                  >
                    {n.name}, {n.state_id}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {otherCategoriesResolved.length > 0 && (
            <div data-testid="empty-other-categories">
              <div className="text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/50 mb-2">
                {category
                  ? `See other categories in ${city.name}`
                  : `Browse categories in ${city.name}`}
              </div>
              <div className="flex flex-wrap gap-2">
                {otherCategoriesResolved.slice(0, 6).map((c) => (
                  <Link
                    key={c.key}
                    to={`/${c.urlSlug}/${city.slug}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold bg-white border border-brand-border-warm text-brand-ink hover:bg-brand-light-green/40 transition-colors"
                  >
                    {c.seoLabel || c.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-brand-border-warm/60">
            <div className="text-sm text-brand-ink/70">
              Know a great spot?{" "}
              <Link
                to="/suggest"
                className="text-brand-forest font-semibold hover:underline"
                data-testid="empty-suggest-place"
              >
                Suggest a place →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



/**
 * Static, SEO-targeted landing page for a single city.
 *
 * Supports four URL shapes:
 *   - /{city-slug}                       (new canonical)        e.g. /tampa-fl
 *   - /{category-slug}/{city-slug}       (new canonical)        e.g. /dog-parks/tampa-fl
 *   - /fl/{slug}                         (legacy, kept indexed) e.g. /fl/tampa
 *   - /fl/{slug}/{category-key}          (legacy)               e.g. /fl/tampa/restaurant
 *
 * When the user is on a legacy URL we emit <link rel="canonical"> pointing
 * to the new URL so Google consolidates ranking signals to one canonical.
 *
 * Cities are configured in /app/frontend/src/lib/cities.js.
 * Categories are configured in /app/frontend/src/lib/categories.js.
 */
const CityPage = ({ stateCode }) => {
  const params = useParams();

  // --- Route disambiguation -------------------------------------------
  // The new root route uses `:citySlug` / `:categorySlug`. The legacy
  // route uses `:slug` / `:category` and is wrapped with a fixed
  // stateCode. Pull both shapes and resolve.
  const isLegacy = !!stateCode;
  let city = null;
  let cat = null;
  let invalidRoute = false;

  if (isLegacy) {
    city = getCity(stateCode, params.slug);
    if (params.category) {
      cat = getCategory(params.category);
      // getCategory falls back to "park" on miss — detect real miss:
      if (cat.key !== params.category) {
        invalidRoute = true;
        cat = null;
      }
    }
  } else {
    // New root route — `:citySlug` is always the LAST segment.
    const { citySlug, categorySlug } = params;
    if (categorySlug) {
      cat = getCategoryByUrlSlug(categorySlug);
      if (!cat) invalidRoute = true;
    }
    if (!invalidRoute) {
      city = getCityBySlug(citySlug);
    }
  }

  const isCategoryPage = !!cat;

  // The Private Parks "category" lives outside the Google Places URL
  // scheme — it's a separate marketplace surface. We expose it as a
  // `?view=private` query param appended to the current city URL so:
  //   - the city context (and SEO canonical) is preserved
  //   - the other category Links naturally clear it (different URL)
  //   - refresh/bookmark survives via the URL
  const [searchParams] = useSearchParams();
  const isPrivateView = searchParams.get("view") === "private";

  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Visual heading shown in the page hero — no brand suffix.
  const displayTitle = useMemo(() => {
    if (!city) return "";
    return isCategoryPage
      ? `${cat.seoLabel} in ${city.name}, ${city.state}`
      : `Dog-Friendly Places in ${city.name}, ${city.state}`;
  }, [city, cat, isCategoryPage]);

  // Browser-tab + <title> string — matches the SEO spec exactly. The
  // " | Pawground" brand suffix lives here only; never bake it into the
  // H1 or share-card text.
  const seoTitle = useMemo(() => {
    if (!city) return "";
    return isCategoryPage
      ? `${cat.seoLabel} in ${city.name}, ${city.state} | Pawground`
      : `Dog Parks & Pet-Friendly Places in ${city.name}, ${city.state} | Pawground`;
  }, [city, cat, isCategoryPage]);

  // --- Puerto Rico bilingual SEO content -----------------------------
  // Emergent's native "Enable SEO" build serves React-rendered HTML to
  // Googlebot — it bypasses our Python /api/seo/render fallback. So the
  // EN+ES blurb pair for PR city × category combos must live in the
  // React tree to land in the crawled HTML. For the city hub (no
  // category) we default to the "park" entry as the most generic intro.
  const isPR = isPRCity(city);
  const prCategoryKey = isPR ? (isCategoryPage ? cat.key : "park") : null;
  const prBilingual = isPR ? getPRBilingualIntro(city.slug, prCategoryKey) : null;
  const prCategoryLabelEs = isPR
    ? getCategoryLabelES(prCategoryKey)
    : null;
  const prFeaturedGuide = isPR ? getPRFeaturedGuide(prCategoryKey) : null;

  const seoDescription = useMemo(() => {
    if (!city) return "";
    const base = isCategoryPage
      ? `Find ${cat.seoShort} in ${city.name}, ${city.state} — with live check-ins & real reviews from local dog parents. Free on Pawground.`
      : `Find the best dog parks, pet-friendly restaurants, groomers & vets in ${city.name}, ${city.state} — with live check-ins & real reviews from local dog parents. Free on Pawground.`;
    // PR city pages get a Spanish lead so the SERP snippet signals
    // bilingual intent. Keep total under ~300 chars to avoid Google
    // truncation mid-Spanish.
    if (isPR && prBilingual) {
      const esLead = prBilingual.es.split(/[.!?]\s/)[0];
      return `${base} ${esLead}.`;
    }
    return base;
  }, [city, cat, isCategoryPage, isPR, prBilingual]);

  // The NEW canonical URL is always the source of truth for SEO,
  // regardless of which URL the user actually landed on.
  const canonicalUrl = useMemo(() => {
    if (!city) return null;
    const path = isCategoryPage ? cityCategoryUrl(city, cat.urlSlug) : cityUrl(city);
    return `${SITE_URL}${path}`;
  }, [city, cat, isCategoryPage]);

  // --- Title, meta, canonical -----------------------------------------
  useEffect(() => {
    if (!city) return;
    document.title = seoTitle;

    // Helper to set or create a single tag.
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        if (selector.startsWith("meta[")) {
          el = document.createElement("meta");
          const propMatch = selector.match(/property="([^"]+)"/);
          const nameMatch = selector.match(/name="([^"]+)"/);
          if (propMatch) el.setAttribute("property", propMatch[1]);
          else if (nameMatch) el.setAttribute("name", nameMatch[1]);
        } else if (selector.startsWith("link[")) {
          el = document.createElement("link");
          const relMatch = selector.match(/rel="([^"]+)"/);
          if (relMatch) el.setAttribute("rel", relMatch[1]);
        } else {
          return;
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", seoDescription);
    if (canonicalUrl) setMeta('link[rel="canonical"]', "href", canonicalUrl);
    setMeta('meta[property="og:title"]', "content", seoTitle);
    setMeta('meta[property="og:description"]', "content", seoDescription);
    if (canonicalUrl) setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "content", seoTitle);
    setMeta('meta[name="twitter:description"]', "content", seoDescription);
  }, [city, seoTitle, seoDescription, canonicalUrl]);

  useEffect(() => {
    if (!city) return;
    setLoading(true);
    api
      .get("/places", {
        params: {
          q: city.query,
          category: isCategoryPage ? cat.key : "all",
          limit: 20,
          // SEO-page guard (FIX 3 from the API-cost incident). City +
          // category surfaces are the highest-volume Googlebot target,
          // so we ALWAYS serve them read-only from MongoDB. A crawler
          // sweep over /dog-parks/tampa-fl, /vets/tampa-fl, … etc must
          // never blow up our Google Places bill, even when the cache
          // has gaps. Real users hitting /search (live data) still
          // path through /api/places without this flag and get
          // fresh-on-miss behavior.
          source: "seo",
        },
      })
      .then((r) => setParks(r.data || []))
      .catch(() => setParks([]))
      .finally(() => setLoading(false));
  }, [city, isCategoryPage, cat]);

  // Private-park listings for this city. Shown in three cases:
  //   1. The "all" view (no category in URL) — surfaces them on the
  //      generic city hub so browsing users discover the marketplace.
  //   2. The "park" category view — they're a parks-adjacent option.
  //   3. The explicit `?view=private` filter — the user tapped the
  //      Private chip on this page, so we hide the Google-Places
  //      list entirely and only render private listings.
  // For any other category (vets, groomers, etc.) the listings are
  // irrelevant and would confuse browsers, so we suppress them.
  const privateParks = useMemo(() => {
    if (!city) return [];
    if (isPrivateView) return getPrivateParksByCity(city.slug);
    if (isCategoryPage && cat && cat.key !== "park") return [];
    return getPrivateParksByCity(city.slug);
  }, [city, isCategoryPage, cat, isPrivateView]);

  // --- Bailouts -------------------------------------------------------
  if (invalidRoute) {
    return <Navigate to="/search" replace />;
  }
  if (!city) {
    // For the new root route, the slug might just be an unknown path —
    // safer to redirect to /search than 404, since RESERVED_SLUGS is
    // already filtered in getCityBySlug.
    const slug = params.citySlug || params.slug;
    if (slug && RESERVED_SLUGS.has(slug.toLowerCase())) {
      // This shouldn't happen (router puts reserved routes first), but
      // bail out cleanly just in case.
      return <Navigate to="/" replace />;
    }
    return <Navigate to="/search" replace />;
  }

  // --- JSON-LD --------------------------------------------------------
  const itemListSchema = parks.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: displayTitle,
        url: canonicalUrl,
        numberOfItems: parks.length,
        itemListElement: parks.slice(0, 10).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE_URL}${placeUrl(p)}`,
          name: p.name,
        })),
      }
    : null;

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: displayTitle,
    description: seoDescription,
    url: canonicalUrl,
    isPartOf: { "@type": "WebSite", name: "Pawground", url: SITE_URL },
    about: {
      "@type": "Place",
      name: `${city.name}, ${city.region}`,
      address: {
        "@type": "PostalAddress",
        addressLocality: city.name,
        addressRegion: city.state,
        addressCountry: city.country,
      },
      // Geo is optional — only featured cities have explicit lat/lng.
      ...(city.lat && city.lng
        ? {
            geo: {
              "@type": "GeoCoordinates",
              latitude: city.lat,
              longitude: city.lng,
            },
          }
        : {}),
    },
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)]" data-testid="city-page">
      <JsonLd schema={pageSchema} id="city-page-schema" />
      {itemListSchema && <JsonLd schema={itemListSchema} id="city-itemlist-schema" />}
      <section className="relative overflow-hidden bg-brand-forest text-white" data-testid="city-hero">
        {city.heroImage && (
          <img
            src={city.heroImage}
            alt={`${city.name}, ${city.region}`}
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
        )}
        {!city.heroImage && (
          <div className="absolute inset-0 opacity-15 pointer-events-none" aria-hidden>
            <MapPin className="absolute top-8 left-8 w-24 h-24" strokeWidth={1.4} />
            <MapPin className="absolute bottom-6 right-10 w-32 h-32 rotate-12" strokeWidth={1.4} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-forest/40 via-brand-forest/60 to-brand-forest" aria-hidden />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-12 md:pt-10 md:pb-16">
          {/* Breadcrumbs — emits visual trail + BreadcrumbList JSON-LD.
              On the hero band we wrap the visual trail in a light pill so
              it stays legible on the dark forest-green background. */}
          <div className="text-white/85 [&_nav]:!text-white/80 [&_a]:!text-white [&_span[aria-current]]:!text-white">
            <Breadcrumbs
              items={
                isCategoryPage
                  ? [
                      { name: city.name, to: cityUrl(city) },
                      { name: cat.seoLabel },
                    ]
                  : [{ name: `${city.name}, ${city.state}` }]
              }
            />
          </div>
          {/* Hub backlink — only on category-scoped city pages (e.g.
              /dog-parks/tampa-fl), pointing back to the national hub
              (/dog-parks). Closes the internal-link graph triangle:
              hub → city+cat → other-cities-same-cat → hub. Plain <a>
              tag (not React-Router <Link>) so the href is present in
              the SSR HTML the crawler parses — react-router would
              also render an <a>, but the explicit tag matches the
              spec example and avoids depending on react-router's
              SSR-friendly behavior. Category label sourced from
              cat.seoLabel so it stays consistent with the H1, the
              breadcrumb, and the hub page itself. */}
          {isCategoryPage && (
            <a
              href={`/${cat.urlSlug}`}
              data-testid="city-hub-backlink"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-white transition-colors mt-1"
            >
              ← All {cat.seoLabel} in the US
            </a>
          )}
          <Link
            to="/search"
            data-testid="city-back-to-search"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-white transition-colors mt-1"
          >
            ← Explore all places
          </Link>

          <span className="mt-5 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
            <MapPin className="w-3.5 h-3.5" />
            {city.region}
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-5 leading-[1.05] tracking-tight"
            data-testid="city-h1"
          >
            {displayTitle}
          </h1>
          {/* Spanish H2 subheading — only for Puerto Rico city pages.
              Renders as plain text below the English H1 so bilingual
              users immediately see Spanish above the fold and Googlebot
              picks up the ES variant in the SSR HTML. */}
          {isPR && prBilingual && (
            <h2
              className="font-heading text-lg sm:text-xl font-semibold text-white/90 mt-2"
              data-testid="city-h2-es"
              lang="es"
            >
              {prCategoryLabelEs} en {city.name}, {city.state}
            </h2>
          )}
          {city.tagline && !isCategoryPage && (
            <div className="mt-3 text-base sm:text-lg text-white/85 font-semibold" data-testid="city-tagline">
              {city.tagline}
            </div>
          )}
          {isPR && prBilingual ? (
            <>
              <p
                className="text-base sm:text-lg text-white/85 mt-5 max-w-3xl leading-relaxed"
                data-testid="city-blurb"
                lang="en"
              >
                {prBilingual.en}
              </p>
              <div className="mt-3 flex items-center gap-3 text-white/40" aria-hidden>
                <span className="h-px flex-1 bg-white/20" />
                <span className="text-xs">🐾</span>
                <span className="h-px flex-1 bg-white/20" />
              </div>
              <p
                className="text-base sm:text-lg text-white/85 mt-3 max-w-3xl leading-relaxed"
                data-testid="city-blurb-es"
                lang="es"
              >
                {prBilingual.es}
              </p>
            </>
          ) : (
            <p
              className="text-base sm:text-lg text-white/85 mt-5 max-w-3xl leading-relaxed"
              data-testid="city-blurb"
            >
              {isCategoryPage ? (
                <>
                  Looking for {cat.seoShort} in {city.name}? Pawground combines fresh listings
                  from Google with real check-ins and reviews from local dog parents so you
                  can pick the right spot before you go. {city.blurb}
                </>
              ) : (
                city.blurb
              )}
            </p>
          )}
        </div>
      </section>

      {/* Featured bilingual guide callout — PR-only. Links to the
          hand-curated EN+ES guide that matches the current category
          (parks vs restaurants), defaulting to the PR dog-parks guide
          for everything else. Sits between hero and category row so
          it's the first thing below the bilingual intro paragraphs. */}
      {isPR && prFeaturedGuide && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
          <Link
            to={`/guides/${prFeaturedGuide.slug}`}
            data-testid="city-pr-featured-guide"
            className="block bg-white border border-brand-border-warm rounded-3xl p-5 sm:p-6 shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl flex-shrink-0"
                style={{ backgroundColor: "#4A7C59" }}
                aria-hidden
              >
                📖
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-forest/70 mb-1">
                  Featured Guide · Guía Destacada
                </div>
                <div className="font-heading text-base sm:text-lg font-bold text-brand-ink leading-snug" lang="en">
                  {prFeaturedGuide.titleEn}
                </div>
                <div className="font-heading text-sm sm:text-base font-semibold text-brand-ink/70 leading-snug" lang="es">
                  {prFeaturedGuide.titleEs}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-brand-forest flex-shrink-0" />
            </div>
          </Link>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-2">
        <div data-testid="city-category-row">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-ink/50 mb-3">
            {isCategoryPage ? "Switch category" : "Jump to a category"}
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const isActive = !isPrivateView && isCategoryPage && c.key === cat.key;
              const href = isActive ? cityUrl(city) : cityCategoryUrl(city, c.urlSlug);
              return (
                <Link
                  key={c.key}
                  to={href}
                  data-testid={`city-cat-${c.key}`}
                  aria-pressed={isActive}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full border pl-1.5 pr-3.5 py-1 text-sm font-semibold transition-all active:scale-[0.97]",
                    isActive
                      ? "text-white border-transparent shadow-warm"
                      : "bg-white text-brand-ink border-brand-border-warm hover:shadow-warm hover:-translate-y-0.5",
                  ].join(" ")}
                  style={isActive ? { backgroundColor: c.color } : undefined}
                >
                  <span
                    className={[
                      "w-6 h-6 rounded-full flex items-center justify-center text-white",
                      isActive ? "bg-white/25" : "",
                    ].join(" ")}
                    style={!isActive ? { backgroundColor: c.color } : undefined}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={2.6} />}
                  </span>
                  {c.label}
                </Link>
              );
            })}
            {/* Private Parks chip — not a Google Places category, so it
                uses a `?view=private` query param appended to the current
                city URL instead of its own /{slug}/{city} route. Active
                state mirrors the other chips (filled green background).
                Tapping again on the active chip clears the filter and
                returns to the city's default "all" view. */}
            {(() => {
              const baseHref = isCategoryPage ? cityCategoryUrl(city, cat.urlSlug) : cityUrl(city);
              const inactiveHref = `${baseHref}?view=private`;
              return (
                <Link
                  key="private"
                  to={isPrivateView ? baseHref : inactiveHref}
                  data-testid="city-cat-private"
                  aria-pressed={isPrivateView}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full border pl-1.5 pr-3.5 py-1 text-sm font-semibold transition-all active:scale-[0.97]",
                    isPrivateView
                      ? "text-white border-transparent shadow-warm"
                      : "bg-white text-brand-ink border-brand-border-warm hover:shadow-warm hover:-translate-y-0.5",
                  ].join(" ")}
                  style={isPrivateView ? { backgroundColor: "#4A7C59" } : undefined}
                >
                  <span
                    className={[
                      "w-6 h-6 rounded-full flex items-center justify-center text-white",
                      isPrivateView ? "bg-white/25" : "",
                    ].join(" ")}
                    style={!isPrivateView ? { backgroundColor: "#4A7C59" } : undefined}
                  >
                    <span className="text-[13px] leading-none" aria-hidden>🏡</span>
                  </span>
                  Private
                  {/* "✨ New" badge — temporary launch tag so existing
                      users notice the new chip. Remove after ~3-4 weeks
                      once organic discovery has settled. Background
                      stays light-green even when the chip itself is
                      active, so the badge reads as a label not part
                      of the active-state styling. */}
                  <span
                    data-testid="private-new-badge"
                    className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase"
                    style={{ backgroundColor: "#E8F2EC", color: "#4A7C59" }}
                  >
                    ✨ New
                  </span>
                </Link>
              );
            })()}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-ink">
            {isPrivateView
              ? `Private parks in ${city.name}`
              : isCategoryPage
                ? `Top ${cat.seoShort} in ${city.name}`
                : `Top dog-friendly places in ${city.name}`}
          </h2>
          {!isPrivateView && !loading && parks.length > 0 && (
            <Link
              to={
                isCategoryPage && cat
                  ? `/search?q=${encodeURIComponent(city.query)}&category=${encodeURIComponent(cat.key)}`
                  : `/search?q=${encodeURIComponent(city.query)}`
              }
              data-testid="city-see-all-link"
              className="text-sm font-semibold text-brand-forest hover:text-brand-forest-dark inline-flex items-center gap-1.5"
            >
              See all on the map <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        <div className="space-y-3" data-testid="city-results-list">
          {/* Sponsored Results — pilot band that appears above organic
              listings on category pages where an eligible sponsor
              serves this city. The component fetches its own data and
              returns null when no sponsor matches — no empty state
              per the pilot spec. Deliberately scoped to `isCategoryPage`
              so it never fires on the bare city page or on the
              private-parks filter view. */}
          {isCategoryPage && cat && !isPrivateView && (
            <SponsoredResults city={city.name} state={city.state} categoryKey={cat.key} />
          )}
          {isPrivateView ? (
            // ---- Private-only filter view ----------------------------
            // Skip the loading branch entirely (privateParks is sync,
            // pulled from the static registry). Show empty state with
            // a link to the global /private-parks index when this city
            // has no listings yet — preserves discoverability of the
            // marketplace even from cities without active hosts.
            privateParks.length === 0 ? (
              <div
                className="bg-white rounded-3xl p-8 text-center border border-brand-border-warm"
                data-testid="city-private-empty"
              >
                <div className="font-heading text-lg font-semibold text-brand-ink">
                  No private parks in {city.name} yet
                </div>
                <p className="text-sm text-brand-ink/60 mt-2 leading-relaxed">
                  We're onboarding hosts city by city.{" "}
                  <Link to="/private-parks" className="text-brand-forest font-semibold hover:underline">
                    Browse all private parks
                  </Link>{" "}
                  or{" "}
                  <Link to="/private-parks/host" className="text-brand-forest font-semibold hover:underline">
                    list your yard
                  </Link>{" "}
                  to be one of the first.
                </p>
              </div>
            ) : (
              privateParks.map((pp) => <PrivateParkCard key={pp.slug} park={pp} />)
            )
          ) : loading ? (
            <div className="flex items-center gap-2 text-brand-ink/60">
              <Loader2 className="w-4 h-4 animate-spin" />
              {isCategoryPage
                ? `Loading ${cat.seoShort} in ${city.name}…`
                : `Loading places in ${city.name}…`}
            </div>
          ) : parks.length === 0 ? (
            <EmptyStateHelp
              city={city}
              category={isCategoryPage ? cat : null}
            />
          ) : (
            <>
              {privateParks.map((pp) => (
                <PrivateParkCard key={pp.slug} park={pp} />
              ))}
              {parks.slice(0, 12).map((p) => <ParkCard key={p.id} park={p} />)}
            </>
          )}
        </div>

        {/* Sparse-results CTA: when a city + category page surfaces <5
            real places, surface the suggestion funnel. Hidden on the
            empty-state branch above (which uses the dedicated
            `SuggestPlaceForm` empty-state card) and on private-park
            views which intentionally only render hand-curated yards. */}
        {!isPrivateView && !loading && parks.length > 0 && parks.length < 5 && (
          <SuggestPlaceBanner context={isCategoryPage ? "city_category" : "city"} />
        )}

        {!isPrivateView && !loading && parks.length > 0 && (
          <div className="mt-10 bg-brand-forest text-white rounded-3xl p-7 sm:p-9 text-center shadow-warm-lg">
            <h2 className="font-heading text-2xl sm:text-3xl font-black leading-tight">
              Know a great dog-friendly spot in {city.name}?
            </h2>
            <p className="text-white/85 mt-3 max-w-xl mx-auto">
              Pawground is built by local dog parents. Sign up and add real check-ins, reviews,
              and busy reports for your favorite spots.
            </p>
            <Link
              to="/signup"
              data-testid="city-signup-cta"
              className="inline-flex items-center gap-2 mt-5 bg-white text-brand-forest font-bold rounded-full px-7 py-3 text-base hover:bg-brand-cream transition-colors active:scale-95"
            >
              Create your free account
            </Link>
          </div>
        )}

        {/* Suggest a place — shown ONLY for cities with no curated content
            yet (templated). For featured cities we already have hand-curated
            data, so the form would be noise. Shown regardless of loading
            state since the form doesn't depend on parks data. */}
        {city.templated && (
          <div className="mt-10" data-testid="city-suggest-place-wrap">
            <SuggestPlaceForm city={city} />
          </div>
        )}

        {/* Same-category-nearby-cities (only on category pages) — links the
            user to e.g. "Dog Parks in Brandon" / "Dog Parks in Riverview"
            when they're on /dog-parks/tampa-fl. Strengthens topical SEO
            graph: same category cluster across nearby cities.

            Hidden on the empty-state branch (parks.length === 0 &&
            !loading) because the new EmptyStateHelp card already surfaces
            the same information as its "Browse nearby" chips, filtered
            more strictly (only cities whose seo_listings tile clears
            MIN_PLACES_TO_INDEX). Showing both duplicates the intent AND
            downgrades quality — this static list will happily link to
            another thin-content page since it doesn't check content
            counts.  */}
        {isCategoryPage && !(parks.length === 0 && !loading) && (() => {
          const near = getNearbyCitiesInState(city, 6);
          if (!near.length) return null;
          return (
            <div className="mt-12" data-testid="city-same-cat-nearby">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-brand-ink mb-4">
                {cat.seoLabel} in nearby cities
              </h2>
              <div className="flex flex-wrap gap-2">
                {near.map((c) => (
                  <Link
                    key={c.slug}
                    to={cityCategoryUrl(c, cat.urlSlug)}
                    data-testid={`city-same-cat-${c.slug}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-brand-border-warm hover:border-brand-forest hover:bg-brand-light-green rounded-full px-4 py-2 text-sm font-semibold text-brand-ink transition-all"
                  >
                    {cat.seoLabel} in {c.name}
                    <ArrowRight className="w-3.5 h-3.5 text-brand-forest" />
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Nearby cities (same state) — every city hub links to 5–8 sibling
            hubs so the internal-link graph stays dense. */}
        {(() => {
          const near = getNearbyCitiesInState(city, 8);
          if (!near.length) return null;
          return (
            <div className="mt-12" data-testid="city-nearby-cities">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-brand-ink mb-4">
                Other dog-friendly cities in {city.region}
              </h2>
              <div className="flex flex-wrap gap-2">
                {near.map((c) => (
                  <Link
                    key={c.slug}
                    to={cityUrl(c)}
                    data-testid={`city-nearby-${c.slug}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-brand-border-warm hover:border-brand-forest hover:bg-brand-light-green rounded-full px-4 py-2 text-sm font-semibold text-brand-ink transition-all"
                  >
                    <MapPin className="w-3.5 h-3.5 text-brand-forest" />
                    {c.name}{c.state !== "PR" ? `, ${c.state}` : ""}
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {/* All-state mega-menu — collapsed by default; lists EVERY city
            in this state for maximum internal-link equity to long-tail
            hubs. Single source page that Google can crawl to discover
            ~70+ FL or ~20+ PR/CA/TX hubs at once. */}
        {(() => {
          const all = getAllCitiesInState(city);
          if (all.length <= 8) return null; // Skip if "nearby" already showed everything.
          return (
            <details
              data-testid="city-all-state-mega"
              className="mt-12 group bg-white rounded-3xl border border-brand-border-warm overflow-hidden"
            >
              <summary className="cursor-pointer list-none px-6 py-5 hover:bg-brand-light-green/40 transition-colors flex items-center justify-between gap-3 font-heading text-base sm:text-lg font-bold text-brand-ink">
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-brand-forest" />
                  All dog-friendly cities in {city.region} ({all.length})
                </span>
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-forest flex items-center gap-1.5">
                  Show all
                  <span className="inline-block group-open:rotate-180 transition-transform">▾</span>
                </span>
              </summary>
              <div className="px-6 pb-6 pt-1">
                <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-1.5 text-sm">
                  {all.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={cityUrl(c)}
                        data-testid={`city-all-state-${c.slug}`}
                        className={`block py-1 ${
                          c.slug === city.slug
                            ? "text-brand-forest font-bold"
                            : "text-brand-ink/80 hover:text-brand-forest hover:underline"
                        }`}
                      >
                        {c.name}
                        {c.slug === city.slug && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wider">(you're here)</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          );
        })()}
      </section>
    </div>
  );
};

export default CityPage;
